/**
 * Purpose-centered nav keys ↔ domain menu keys for RBAC visibility filtering.
 *
 * Purpose areas remount under `nav-area-*` / `nav-layer-*` keys while
 * `isBusinessMenuKeyVisible` / `visibleDomainKeys` remain domain-keyed
 * (`workspace`, `flow`, `system`, …). This bridge prevents legitimate menus
 * from disappearing after the purpose-centered remount.
 */

import {
  ADVANCED_NAVIGATION_LAYERS_V0,
  PRIMARY_NAVIGATION_AREAS_V0,
} from './primaryNavigationCanon';

/** Explicit domain aliases for purpose / advanced layer nav keys. */
export const PURPOSE_NAV_KEY_TO_DOMAIN_KEYS: Readonly<Record<string, readonly string[]>> = {
  'nav-area-today': ['workspace'],
  'nav-area-workbench': ['workspace'],
  'nav-area-operations': ['flow', 'manufacturing', 'finance', 'log'],
  'nav-area-objects': ['item', 'space', 'knowledge', 'calendar', 'appeal'],
  'nav-area-evidence-decisions': ['flow', 'system'],
  'nav-area-people-authority': ['entity'],
  'nav-area-automation': ['automation'],
  'nav-layer-configure': ['system', 'export', 'finance-advanced'],
  'nav-layer-operations-console': ['conscious'],
};

/** Canon routeGroups fallback — keeps bridge aligned with area definitions. */
function domainKeysFromCanonRouteGroups(navKey: string): readonly string[] | undefined {
  const area = [...PRIMARY_NAVIGATION_AREAS_V0, ...ADVANCED_NAVIGATION_LAYERS_V0].find(
    (entry) => entry.navKey === navKey
  );
  if (!area) return undefined;
  return area.routeGroups
    .map((group) => {
      if (group === 'workspace-tasks' || group === 'workspace-operations') return 'workspace';
      if (group === 'physical-access') return 'workspace';
      if (group === 'approvals' || group === 'document-review') {
        return group === 'approvals' ? 'flow' : 'system';
      }
      if (group === 'settings' || group === 'settings-authority') return 'system';
      if (group === 'warehouse' || group === 'inventory') {
        return group === 'warehouse' ? 'flow' : 'item';
      }
      if (group === 'pilot-operator-support') return 'conscious';
      return group;
    })
    .filter((key, index, all) => all.indexOf(key) === index);
}

/**
 * True when `menuKey` is itself a visible domain key, or a purpose/layer key
 * whose aliased domain keys intersect `visibleDomainKeys`.
 */
export function isPurposeOrDomainMenuKeyVisible(
  menuKey: string,
  visibleDomainKeys: readonly string[]
): boolean {
  if (visibleDomainKeys.includes(menuKey)) return true;
  const aliases =
    PURPOSE_NAV_KEY_TO_DOMAIN_KEYS[menuKey] ?? domainKeysFromCanonRouteGroups(menuKey);
  if (!aliases?.length) return false;
  return aliases.some((domainKey) => visibleDomainKeys.includes(domainKey));
}
